import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Server } from "lucide-react";

const TONE = {
  running: "border-emerald-200 bg-emerald-50 text-emerald-800",
  went_quiet: "border-red-200 bg-red-50 text-[#B01E28]",
  stopped: "border-slate-200 bg-slate-50 text-slate-600",
};
const WORD = { running: "Checking in", went_quiet: "Went quiet", stopped: "Stopped" };
const ENDED = { release: ["at a release", "border-slate-200 bg-white text-slate-600"], unexpected: ["unexpectedly", "border-red-300 bg-white text-[#B01E28]"] };
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const upFor = (a, b) => {
  const m = Math.max(0, Math.round((new Date(b || Date.now()) - new Date(a)) / 60000));
  return m < 60 ? `${m} min` : m < 2880 ? `${Math.round(m / 60)} h` : `${Math.round(m / 1440)} days`;
};
const pause = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`);

// EACH SERVER COPY KEEPS ITS OWN LOG (stability batch): when each copy started, its last check-in, and the longest
// moment it couldn't answer anyone — so "since when?" never depends on platform events that expire in an hour.
function CopyRow({ c, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`server-copy-${i}`} className="rounded-lg border border-slate-200">
      <button type="button" data-testid={`server-copy-toggle-${i}`} onClick={() => setOpen((v) => !v)}
        className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors">
        <span data-testid={`server-copy-state-${i}`} className={`rounded-md border px-2 py-0.5 text-[11px] font-bold ${TONE[c.state]}`}>{WORD[c.state]}</span>
        {c.ended && <span data-testid={`server-copy-ended-${i}`} className={`rounded-md border px-2 py-0.5 text-[11px] font-bold ${ENDED[c.ended][1]}`}>ended {ENDED[c.ended][0]}</span>}
        <b className="font-mono">{c.name}</b>{c.this_copy && <span className="text-[11px] text-slate-500">(answering you now)</span>}
        <span className="text-[12px] text-slate-600">started {when(c.started_at)} · {c.state === "running" ? `up ${upFor(c.started_at)}` : `${c.state === "stopped" ? "stopped" : "last heard"} ${when(c.stopped_at || c.last_seen_at)}`}</span>
        <span data-testid={`server-copy-hour-${i}`} className="text-[12px] text-slate-600 sm:ml-auto">
          last hour: longest pause {pause(c.hour.worst_pause_ms)} · {c.hour.slow_requests} slow of {c.hour.requests} · {c.memory_mb ?? "—"} MB
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-3 py-2 text-[12px] text-slate-600 space-y-1">
          <div data-testid={`server-copy-day-${i}`}>Last 24 h: {c.day.pauses_over_1s} check-in(s) with a pause over 1 s · longest {pause(c.day.worst_pause_ms)} · {c.day.slow_requests} slow request(s) · version {c.version || "—"} · {c.checkins} check-ins</div>
          <div data-testid={`server-copy-freezes-${i}`}>Last 24 h: {c.day.freezes ?? 0} freeze(s) over 1 s{c.day.longest_freeze ? ` · longest ${pause(c.day.longest_freeze.ms)} while answering ${c.day.longest_freeze.routes?.join(", ") || "nobody"}${c.day.longest_freeze.jobs?.length ? ` · timed jobs running: ${c.day.longest_freeze.jobs.join(", ")}` : ""}` : ""}</div>
          {c.recent.map((r) => (
            <div key={r.at} className="font-mono text-[11px] text-slate-500">
              {when(r.at)} · pause {pause(r.worst_pause_ms)} · {r.requests} req · slowest {r.slowest_ms} ms · {r.memory_mb} MB{r.jobs?.length ? ` · running ${r.jobs.join(", ")}` : ""}
              {(r.freezes || []).map((f) => (
                <div key={f.at} className="pl-4 text-[#B01E28]">froze {pause(f.ms)} at {when(f.at)} · answering {f.routes?.join(", ") || "nobody"}{f.jobs?.length ? ` · jobs ${f.jobs.join(", ")}` : ""}</div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ServerCopiesCard() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [all, setAll] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/server-copies").then(({ data: d }) => { setData(d); setFailed(false); }).catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data && !failed) return null;
  const shown = all ? data?.copies || [] : (data?.copies || []).slice(0, 20);
  return (
    <div data-testid="server-copies-card" className="border border-slate-200 bg-white rounded-xl shadow-sm mb-5 overflow-hidden">
      <div className="bg-slate-100 px-4 py-1 font-mono text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
        <Server className="w-3.5 h-3.5 text-[#B01E28]" />Server copies — each one's own log, last 7 days
        <button data-testid="server-copies-refresh" onClick={load} className="ml-auto h-11 px-3 normal-case font-sans text-xs text-slate-500 hover:text-[#B01E28] transition-colors">Refresh</button>
      </div>
      <div className="p-4 space-y-2">
        {failed && <p data-testid="server-copies-error" className="text-sm text-amber-800">The server copies' log couldn't be read just now. Tap Refresh to try once more.</p>}
        <p className="text-[12px] text-slate-500">Each copy of the server writes down when it started and checks in every minute. A copy that stops checking in without saying goodbye shows as “Went quiet”. A pause is a moment the copy couldn’t answer anyone.</p>
        {data?.ends && (
          <p data-testid="server-copies-ends" className="text-[12px] text-slate-700">
            Last 7 days: <b>{data.ends.at_release}</b> ended at a release (new copies took over) · <b className={data.ends.unexpected ? "text-[#B01E28]" : ""}>{data.ends.unexpected}</b> ended unexpectedly{data.ends.unexpected ? ` — ${data.ends.unexpected_at.slice(0, 12).map(when).join(", ")}${data.ends.unexpected > 12 ? " …" : ""}` : ""}
          </p>
        )}
        {data && data.copies.length === 0 && <p data-testid="server-copies-empty" className="text-sm text-slate-500">No check-ins yet.</p>}
        {shown.map((c, i) => <CopyRow key={`${c.name}-${c.started_at}`} c={c} i={i} />)}
        {data && data.copies.length > 20 && (
          <button type="button" data-testid="server-copies-show-all" onClick={() => setAll((x) => !x)}
            className="h-11 px-4 rounded-lg border-2 border-slate-300 bg-white text-sm font-bold hover:bg-slate-50 transition-colors">
            {all ? "Show the newest 20" : `Show all ${data.copies.length}`}</button>
        )}
      </div>
    </div>
  );
}
